import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, UserPlus, X, Plus, CalendarDays, Mail, Trash2, Archive, ArchiveRestore, QrCode, Download, Link2, Wallet, Users, Search, User, Swords, Truck, BookUser, Hotel, Navigation, HelpCircle, Check } from "lucide-react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AskQuestionsDialog from "@/components/AskQuestionsDialog";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

const GRADES = [
  { value: "first_aider", label: "First Aider / Medical" },
  { value: "level_3", label: "Level 3" },
  { value: "level_4", label: "Level 4" },
  { value: "technician", label: "Technician (EMT)" },
  { value: "paramedic", label: "Paramedic" },
  { value: "doctor", label: "Doctor" },
];
const gradeRank = (g) => GRADES.findIndex((x) => x.value === g);

const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDay = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";

const spanDays = (ev) => {
  if (!ev?.event_date) return [];
  const out = [];
  const d = new Date(`${ev.event_date}T00:00:00`);
  const end = new Date(`${(ev.end_date && ev.end_date > ev.event_date ? ev.end_date : ev.event_date)}T00:00:00`);
  let guard = 0;
  while (d <= end && guard < 31) {
    out.push(isoDay(d));
    d.setDate(d.getDate() + 1);
    guard += 1;
  }
  return out;
};

function monthCells(cursor) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7; // Monday start
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

export default function Calendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [askClients, setAskClients] = useState([]);

  const openAsk = async () => {
    setAskOpen(true);
    try {
      const { data } = await api.get("/clients");
      setAskClients(data);
    } catch { /* dialog still works without contact autofill */ }
  };
  const [jobNote, setJobNote] = useState("");
  const [linkedTask, setLinkedTask] = useState(null);
  const [jobDate, setJobDate] = useState("");

  useEffect(() => {
    if (!selected?.from_task) { setLinkedTask(null); return; }
    api.get("/crm/tasks").then(({ data }) => {
      setLinkedTask((data || []).find((t) => t.event_id === selected.id || (t.event_ids || []).includes(selected.id) || t.id === selected.task_id) || null);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const moveJob = async () => {
    if (!jobDate || !linkedTask) return;
    try {
      await api.patch(`/crm/tasks/${linkedTask.id}`, { due_date: jobDate });
      const { data } = await api.post(`/crm/tasks/${linkedTask.id}/to-calendar`);
      toast.success(data.message);
      setJobDate("");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markJobDone = async () => {
    try {
      const { data } = await api.post(`/events/${selected.id}/job-done`);
      toast.success(data.message);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addJobNote = async () => {
    if (!jobNote.trim() || !selected) return;
    try {
      const { data } = await api.post(`/events/${selected.id}/job-notes`, { text: jobNote });
      setSelected(data);
      setJobNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [rosterForm, setRosterForm] = useState({ name: "", email: "", time: "", pay: "", hours: "", rate: "", work_date: "", required_grade: "" });
  const [jumpQuery, setJumpQuery] = useState("");
  const [dayAdd, setDayAdd] = useState(null);
  const [dayForm, setDayForm] = useState({ name: "", venue: "", organiser: "", end_date: "", times: "", expected_attendance: "", client_id: "", vehicle_id: "", booking_status: "" });
  const [editForm, setEditForm] = useState({ name: "", venue: "", organiser: "", event_date: "", end_date: "", times: "", expected_attendance: "", vehicle_status: "", vehicle_id: "", client_id: "", booking_status: "" });
  const [accomms, setAccomms] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);
  const [finForm, setFinForm] = useState({ client_charge: "", hotel_cost: "", fuel_cost: "", equipment_cost: "", other_cost: "" });
  const [qrUrl, setQrUrl] = useState("");
  const [emailBusy, setEmailBusy] = useState("");
  const [addForm, setAddForm] = useState({ name: "", venue: "", organiser: "", event_date: "", end_date: "", times: "", expected_attendance: "" });
  const [saving, setSaving] = useState(false);
  const [staffFilter, setStaffFilter] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [vehOpen, setVehOpen] = useState(false);
  const [vehForm, setVehForm] = useState({ name: "", plate: "", kind: "own" });

  const load = useCallback(async () => {
    try {
      const [{ data }, veh] = await Promise.all([api.get("/events"), api.get("/vehicles").catch(() => ({ data: [] }))]);
      setEvents(data.filter((e) => e.id));
      setVehicles(veh.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fetchStaff = useCallback(() => {
    if (user?.role === "admin") {
      api.get("/staff").then((r) => setStaff(r.data.filter((s) => s.active))).catch(() => {});
    }
  }, [user]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => { if (selected) fetchStaff(); }, [selected, fetchStaff]);

  const byDate = {};
  events.forEach((e) => {
    if (!e.event_date) return;
    const start = e.event_date;
    const end = e.end_date && e.end_date > start ? e.end_date : start;
    const d = new Date(`${start}T00:00:00`);
    const endD = new Date(`${end}T00:00:00`);
    let guard = 0;
    while (d <= endD && guard < 31) {
      const k = isoDay(d);
      (byDate[k] = byDate[k] || []).push(e);
      d.setDate(d.getDate() + 1);
      guard += 1;
    }
  });

  const rosterNames = useMemo(
    () => [...new Set(events.flatMap((ev) => (ev.roster || []).map((r) => (r.name || "").trim())).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [events]
  );

  const activeStaff = useMemo(() => {
    const t = staffFilter.trim();
    return rosterNames.includes(t) ? t : "";
  }, [staffFilter, rosterNames]);

  const staffMatches = useMemo(
    () => (staffFilter.trim() ? rosterNames.filter((n) => n.toLowerCase().includes(staffFilter.trim().toLowerCase())) : []),
    [rosterNames, staffFilter]
  );

  const staffJobs = useMemo(
    () => (activeStaff
      ? events
          .flatMap((ev) => (ev.roster || []).filter((r) => (r.name || "").trim() === activeStaff).map((r) => ({ ev, r })))
          .sort((a, b) => (a.r.work_date || a.ev.event_date || "9999").localeCompare(b.r.work_date || b.ev.event_date || "9999"))
      : []),
    [events, activeStaff]
  );

  const jumpMatches = useMemo(
    () => events.filter((ev) => ev.name.toLowerCase().includes(jumpQuery.trim().toLowerCase())),
    [events, jumpQuery]
  );

  const hasStaffOn = (ev, day) => activeStaff && (ev.roster || []).some((r) => (r.name || "").trim() === activeStaff && (!r.work_date || r.work_date === day));

  const organiserForClient = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return "";
    return (c.contacts || []).find((ct) => ct.name)?.name || c.name || "";
  };

  const addEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/events", {
        name: addForm.name, venue: addForm.venue, organiser: addForm.organiser, event_date: addForm.event_date,
        end_date: addForm.end_date, times: addForm.times,
        expected_attendance: addForm.expected_attendance === "" ? null : Number(addForm.expected_attendance),
      });
      toast.success(`Event "${addForm.name}" added`);
      setAddForm({ name: "", venue: "", organiser: "", event_date: "", end_date: "", times: "", expected_attendance: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addRoster = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...rosterForm,
        hours: rosterForm.hours === "" ? null : Number(rosterForm.hours),
        rate: rosterForm.rate === "" ? null : Number(rosterForm.rate),
      };
      const { data } = await api.post(`/events/${selected.id}/roster`, payload);
      setSelected(data);
      toast.success(`${rosterForm.name} booked onto ${selected.name}`);
      setRosterForm({ name: "", email: "", time: "", pay: "", hours: "", rate: "", work_date: "", required_grade: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeRoster = async (entryId) => {
    try {
      const { data } = await api.delete(`/events/${selected.id}/roster/${entryId}`);
      setSelected(data);
      toast.success("Removed from job");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const emailRoster = async (entryId) => {
    setEmailBusy(entryId);
    try {
      const { data } = await api.post(`/events/${selected.id}/roster/${entryId}/email`);
      toast.success(data.message);
      const { data: evts } = await api.get("/events");
      setEvents(evts.filter((x) => x.id));
      setSelected((s) => evts.find((x) => x.id === s?.id) || s);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailBusy("");
    }
  };

  const openEvent = (ev) => {
    setSelected(ev);
    setRosterForm((f) => ({ ...f, work_date: "" }));
    setEditForm({ name: ev.name, venue: ev.venue || "", organiser: ev.organiser || "", event_date: ev.event_date || "", end_date: ev.end_date || "", times: ev.times || "", expected_attendance: ev.expected_attendance ?? "", vehicle_status: ev.vehicle_status || "", vehicle_id: ev.vehicle_id || "", client_id: ev.client_id || "", booking_status: ev.booking_status || "" });
    setAccomms(ev.accommodations || []);
    const fin = ev.finances || {};
    setFinForm({
      client_charge: fin.client_charge ?? "", hotel_cost: fin.hotel_cost ?? "",
      fuel_cost: fin.fuel_cost ?? "", equipment_cost: fin.equipment_cost ?? "",
      other_cost: fin.other_cost ?? "",
    });
    setQrUrl("");
    if (ev.checkin_token) {
      QRCode.toDataURL(`${window.location.origin}/checkin/${ev.checkin_token}`, { width: 640, margin: 2 })
        .then(setQrUrl)
        .catch(() => setQrUrl(""));
    }
  };

  const downloadQr = () => {
    if (!qrUrl || !selected) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `checkin-qr-${selected.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
    a.click();
    toast.success("QR code downloaded — print it or show it on a screen");
  };

  const saveEvent = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      payload.expected_attendance = editForm.expected_attendance === "" ? null : Number(editForm.expected_attendance);
      const { data } = await api.patch(`/events/${selected.id}`, payload);
      setSelected(data);
      setAccomms(data.accommodations || []);
      toast.success("Event updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAccomms = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/events/${selected.id}/accommodation`,
        { accommodations: accomms.filter((h) => h.name.trim()) });
      setSelected(data);
      setAccomms(data.accommodations || []);
      toast.success("Hotel details saved — ticked staff can now see the address");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addEventForDay = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/events", {
        name: dayForm.name, venue: dayForm.venue, organiser: dayForm.organiser, event_date: dayAdd,
        end_date: dayForm.end_date, times: dayForm.times,
        expected_attendance: dayForm.expected_attendance === "" ? null : Number(dayForm.expected_attendance),
        client_id: dayForm.client_id, vehicle_id: dayForm.vehicle_id, booking_status: dayForm.booking_status,
      });
      toast.success(`Event "${dayForm.name}" added`);
      setDayForm({ name: "", venue: "", organiser: "", end_date: "", times: "", expected_attendance: "", client_id: "", vehicle_id: "", booking_status: "" });
      setDayAdd(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveFinances = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of ["client_charge", "hotel_cost", "fuel_cost", "equipment_cost", "other_cost"]) {
        if (finForm[k] !== "") payload[k] = Number(finForm[k]);
      }
      const { data } = await api.patch(`/events/${selected.id}`, payload);
      setSelected(data);
      toast.success("Finances saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async () => {
    try {
      await api.patch(`/events/${selected.id}`, { archived: !selected.archived });
      toast.success(selected.archived ? `"${selected.name}" restored` : `"${selected.name}" archived — its patients are hidden, not deleted`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteEvent = async () => {
    try {
      const { data } = await api.delete(`/events/${selected.id}`);
      toast.success(data.message);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmBooking = async () => {
    try {
      await api.patch(`/events/${selected.id}`, { booking_status: "confirmed" });
      toast.success(`"${selected.name}" accepted — booked in`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const declineBooking = async () => {
    if (!window.confirm(`Decline "${selected.name}"? It will be removed from the calendar.`)) return;
    deleteEvent();
  };

  const addVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vehicles", vehForm);
      toast.success(`${vehForm.name} added to your vehicles`);
      setVehForm({ name: "", plate: "", kind: "own" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeVehicle = async (v) => {
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success(`${v.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const jumpToEvent = (name) => {
    const ev = events.find((x) => x.name === name);
    if (!ev) return;
    if (ev.event_date) setCursor(new Date(`${ev.event_date}T00:00:00`));
    openEvent(ev);
  };

  const today = isoDay(new Date());
  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const selectedVehicle = vehicles.find((v) => v.id === selected?.vehicle_id);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="calendar-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 py-3 flex justify-between items-center shadow-sm">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#005EB8] transition-colors min-h-[44px]">
          <ChevronLeft className="h-4 w-4" /> Dashboard
        </button>
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="WEMS" className="h-8 w-auto" />
          <p className="font-heading font-bold text-[#0F2547] text-sm hidden sm:block">Event Calendar</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-8">
        {user?.role === "admin" && (
        <form onSubmit={addEvent} className="bg-white rounded-xl border border-slate-200 p-4 mb-6 grid grid-cols-2 sm:grid-cols-8 gap-3" data-testid="calendar-add-event-form">
          <input required placeholder="Event name *" value={addForm.name}
            onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="cal-event-name-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <input placeholder="Venue" value={addForm.venue}
            onChange={(e) => setAddForm((f) => ({ ...f, venue: e.target.value }))}            data-testid="cal-event-venue-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <input placeholder="Organiser name" value={addForm.organiser}
            onChange={(e) => setAddForm((f) => ({ ...f, organiser: e.target.value }))}
            data-testid="cal-event-organiser-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <input type="date" required value={addForm.event_date}
            onChange={(e) => setAddForm((f) => ({ ...f, event_date: e.target.value }))}
            data-testid="cal-event-date-input" title="First day"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <input type="date" value={addForm.end_date} min={addForm.event_date || undefined}
            onChange={(e) => setAddForm((f) => ({ ...f, end_date: e.target.value }))}
            data-testid="cal-event-end-date-input" title="Last day (leave blank for a one-day event)"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <input placeholder="Times (e.g. 9–5 both days)" value={addForm.times}
            onChange={(e) => setAddForm((f) => ({ ...f, times: e.target.value }))}
            data-testid="cal-event-times-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <input type="number" min="0" placeholder="Expected attendance" value={addForm.expected_attendance}
            onChange={(e) => setAddForm((f) => ({ ...f, expected_attendance: e.target.value }))}
            data-testid="cal-event-attendance-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <button type="submit" disabled={saving} data-testid="cal-add-event-submit"
            className="h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Add event
          </button>
        </form>
        )}

        {user?.role === "admin" && (
          <div className="flex justify-end mb-2">
            <button onClick={() => setVehOpen(true)} data-testid="calendar-vehicles-btn"
              className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] inline-flex items-center gap-1.5 transition-colors">
              <Truck className="h-4 w-4" /> Vehicles
            </button>
          </div>
        )}

        <div className="flex gap-1 mb-6 overflow-x-auto pb-1" data-testid="calendar-month-jump">
          {Array.from({ length: 14 }, (_, i) => {
            const n = new Date();
            const d = new Date(n.getFullYear(), n.getMonth() + i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            const isCurrent = d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth();
            const hasEvents = Object.keys(byDate).some((k) => k.startsWith(key));
            return (
              <button key={key} onClick={() => setCursor(new Date(d.getFullYear(), d.getMonth(), 1))}
                data-testid={`month-jump-${key}`} title={d.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                className={`flex-1 min-w-[54px] h-14 rounded-lg border flex flex-col items-center justify-center transition-colors ${isCurrent ? "bg-[#005EB8] border-[#005EB8] text-white" : "bg-white border-slate-200 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8]"}`}>
                <span className="text-[11px] font-black tracking-wide">{d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase()}</span>
                <span className={`text-[10px] font-bold ${isCurrent ? "text-sky-100" : "text-slate-400"}`}>{String(d.getFullYear()).slice(2)}</span>
                <span className={`mt-0.5 h-1 w-1 rounded-full ${hasEvents ? (isCurrent ? "bg-white" : "bg-[#007F3B]") : "bg-transparent"}`} />
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-200 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                data-testid="calendar-prev" className="h-10 w-10 rounded-lg border border-slate-300 grid place-items-center hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }}
                data-testid="calendar-today" className="h-10 px-3 rounded-lg border border-slate-300 text-xs font-bold hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                Today
              </button>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                data-testid="calendar-next" className="h-10 w-10 rounded-lg border border-slate-300 grid place-items-center hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
              <h2 className="font-heading text-lg sm:text-xl font-bold text-[#0F2547] ml-2" data-testid="calendar-month-label">{monthLabel}</h2>
            </div>
            <div className="relative" data-testid="calendar-staff-filter">
              <User className="h-4 w-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <input
                value={staffFilter}
                onChange={(e) => setStaffFilter(e.target.value)}
                placeholder="Search staff name…"
                data-testid="calendar-staff-filter-input"
                className="h-10 w-[220px] rounded-lg border border-slate-300 pl-9 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white text-[#0F2547]"
              />
              {staffFilter.trim() && !activeStaff && (
                <div className="absolute right-0 top-full mt-1 w-64 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-30" data-testid="calendar-staff-results">
                  {staffMatches.length === 0 ? (
                    <p className="text-xs text-slate-400 px-3 py-3">Nobody on any event matches "{staffFilter.trim()}"</p>
                  ) : (
                    staffMatches.map((n) => (
                      <button key={n} onClick={() => setStaffFilter(n)}
                        data-testid={`staff-result-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="w-full text-left px-3 py-2.5 text-sm font-semibold text-[#0F2547] hover:bg-sky-50 transition-colors border-b border-slate-100 last:border-0">
                        {n}
                      </button>
                    ))
                  )}
                </div>
              )}
              {activeStaff && (
                <button onClick={() => setStaffFilter("")} aria-label="Clear staff search" data-testid="calendar-staff-filter-clear"
                  className="absolute right-2.5 top-3 text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="relative" data-testid="calendar-event-search">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
              <input
                value={jumpQuery}
                onChange={(e) => setJumpQuery(e.target.value)}
                placeholder="Search events…"
                data-testid="calendar-event-search-input"
                className="h-10 w-[220px] rounded-lg border border-slate-300 pl-9 pr-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white"
              />
              {jumpQuery.trim() && (
                <div className="absolute right-0 top-full mt-1 w-72 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-30" data-testid="calendar-search-results">
                  {jumpMatches.length === 0 ? (
                    <p className="text-xs text-slate-400 px-3 py-3">No events match "{jumpQuery.trim()}"</p>
                  ) : (
                    jumpMatches.map((ev) => (
                        <button
                          key={ev.id || ev.name}
                          onClick={() => { jumpToEvent(ev.name); setJumpQuery(""); }}
                          data-testid={`search-result-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-sky-50 transition-colors border-b border-slate-100 last:border-0"
                        >
                          <span className="font-semibold text-[#0F2547]">{ev.name}</span>
                          <span className="text-[11px] text-slate-400 ml-2">
                            {ev.event_date ? new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "no date"}
                            {ev.archived ? " · archived" : ""}
                          </span>
                        </button>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-7 text-center text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells(cursor).map((day, i) => {
              if (!day) return <div key={`blank-${i}`} className="min-h-[88px] sm:min-h-[110px] border-b border-r border-slate-100 bg-slate-50/50" />;
              const key = isoDay(day);
              const dayEvents = byDate[key] || [];
              return (
                <div key={key} data-testid={`calendar-day-${key}`}
                  onClick={() => { if (user?.role !== "admin") return; setDayForm({ name: "", venue: "" }); setDayAdd(key); }}
                  title="Click to add an event on this day"
                  className={`min-h-[88px] sm:min-h-[110px] border-b border-r border-slate-100 p-1.5 sm:p-2 cursor-pointer hover:bg-sky-50/60 transition-colors ${key === today ? "bg-sky-50" : ""}`}>
                  <p className={`text-xs font-bold mb-1 ${key === today ? "text-[#005EB8]" : "text-slate-500"}`}>{day.getDate()}</p>
                  <div className="space-y-1">
                    {dayEvents.map((ev) => (
                      <button key={ev.id} onClick={(e) => { e.stopPropagation(); openEvent(ev); }} data-testid={`event-chip-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`group relative w-full text-left ${activeStaff ? (hasStaffOn(ev, key) ? "bg-[#007F3B] hover:bg-[#006432] ring-2 ring-[#00A499]" : `${ev.archived ? "bg-slate-400" : ev.booking_status === "provisional" ? "bg-[#ED8B00]" : ev.from_task ? "bg-[#007F3B]" : "bg-[#0F2547]"} opacity-30 hover:opacity-60`) : ev.archived ? "bg-slate-400 hover:bg-slate-500" : ev.booking_status === "provisional" ? "bg-[#ED8B00] hover:bg-[#D97C00]" : ev.from_task ? "bg-[#007F3B] hover:bg-[#006331]" : "bg-[#0F2547] hover:bg-[#005EB8]"} transition-colors text-white text-[10px] sm:text-[11px] font-semibold rounded-md px-1.5 py-1`}>
                        <span className="block truncate">
                          {ev.name}{ev.archived ? " (archived)" : ""}
                          {(ev.roster_count ?? 0) > 0 && (
                            <span className="ml-1 opacity-75">· {ev.roster_count} staff</span>
                          )}
                          {user?.role === "admin" && Number(ev.finances?.client_charge) > 0 && (
                            <span className={`ml-1 px-1 rounded text-[8px] font-black ${ev.client_paid ? "bg-emerald-400 text-emerald-950" : "bg-amber-400 text-amber-950"}`} data-testid={`chip-paid-${ev.id}`}>
                              {ev.client_paid ? "PAID" : "UNPAID"}
                            </span>
                          )}
                        </span>
                        {((ev.patient_count ?? 0) > 0 || ev.expected_attendance) && (
                          <span className="block text-[9px] opacity-80 font-vitals" data-testid={`event-attendance-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {ev.patient_count ?? 0}{ev.expected_attendance ? ` of ~${ev.expected_attendance}` : ""} patients
                          </span>
                        )}
                        <span data-testid={`event-tooltip-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="hidden group-hover:block absolute left-0 top-full z-30 mt-1 w-52 rounded-lg bg-white border border-slate-200 shadow-lg p-2.5 whitespace-normal">
                          <span className="block text-[11px] font-bold text-[#0F2547] truncate">{ev.name}</span>
                          <span className="block text-[10px] text-slate-500 mt-0.5">
                            {ev.patient_count ?? 0} patients{ev.expected_attendance ? ` of ~${ev.expected_attendance} expected` : ""} · {ev.lost_count ?? 0} lost children · {ev.roster_count ?? 0} staff booked
                          </span>
                          {(ev.roster || []).length > 0 && (
                            <span className="block text-[10px] text-slate-600 mt-0.5" data-testid={`tooltip-staff-${ev.id}`}>
                              Working: {(ev.roster || []).map((r) => r.name).join(", ")}
                            </span>
                          )}
                          {ev.vehicle_status && (
                            <span className="block text-[10px] font-bold text-amber-600 mt-0.5" data-testid={`tooltip-vehicle-${ev.id}`}>
                              {{ own: "Own vehicle", booked: "Vehicle booked in", needed: "Vehicle needed" }[ev.vehicle_status] || ev.vehicle_status}
                            </span>
                          )}
                          {ev.archived && (
                            <span className="block text-[10px] font-bold text-amber-600 mt-0.5">Archived — click to view &amp; restore</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {activeStaff && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4 sm:p-6" data-testid="staff-jobs-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-heading text-lg font-bold text-[#0F2547]" data-testid="staff-jobs-title">
                {activeStaff} — {staffJobs.length} job{staffJobs.length === 1 ? "" : "s"}
              </h3>
              <button onClick={() => setStaffFilter("")} data-testid="staff-jobs-clear"
                className="text-xs font-semibold text-slate-500 hover:text-[#005EB8] transition-colors">
                Clear filter
              </button>
            </div>
            {staffJobs.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="staff-jobs-empty">No shifts booked for {activeStaff}.</p>
            ) : (
              <div className="space-y-2">
                {staffJobs.map(({ ev, r }) => {
                  const amt = r.amount != null ? Number(r.amount) : parseFloat(String(r.pay || "").replace(/[^0-9.]/g, "")) || 0;
                  return (
                    <button key={`${ev.id}-${r.entry_id}`} onClick={() => openEvent(ev)} data-testid={`staff-job-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="w-full text-left rounded-xl border border-slate-200 hover:border-[#005EB8] hover:bg-sky-50/50 transition-colors px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0F2547] text-sm truncate">
                          {ev.name}{ev.archived ? " (archived)" : ""}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {r.work_date
                            ? fmtDay(r.work_date)
                            : ev.event_date
                              ? `${fmtDay(ev.event_date)}${ev.end_date && ev.end_date > ev.event_date ? ` → ${new Date(`${ev.end_date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}` : ""}`
                              : "No date"}
                          {ev.venue ? ` · ${ev.venue}` : ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        {r.time && <p className="text-xs font-semibold text-[#005EB8]">{r.time}</p>}
                        <p className="text-xs text-slate-500">
                          {r.hours && r.rate ? `${r.hours}h @ £${Number(r.rate).toFixed(2)}/h · ` : ""}£{amt.toFixed(2)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="mt-8">
          <Link to="/my" data-testid="calendar-my-shifts-link"
            className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#005EB8] hover:shadow-md transition-all group">
            <div className="h-12 w-12 rounded-xl bg-[#F4F7FA] border border-slate-200 grid place-items-center shrink-0 group-hover:bg-[#005EB8] transition-colors">
              <Wallet className="h-6 w-6 text-[#005EB8] group-hover:text-white transition-colors" />
            </div>
            <div>
              <p className="font-bold text-[#0F2547] text-base">My shifts &amp; pay</p>
              <p className="text-sm text-slate-500 mt-0.5">Your upcoming shifts, wages and availability diary</p>
            </div>
          </Link>
        </div>

      </main>

      <Dialog open={Boolean(dayAdd)} onOpenChange={(v) => !v && setDayAdd(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="day-add-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#005EB8]" /> Add event
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {dayAdd && new Date(`${dayAdd}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={addEventForDay} className="grid gap-2">
            <input required placeholder="Event name *" value={dayForm.name}
              onChange={(e) => setDayForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="day-event-name-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input placeholder="Venue / location" value={dayForm.venue}
              onChange={(e) => setDayForm((f) => ({ ...f, venue: e.target.value }))}
              data-testid="day-event-venue-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input placeholder="Organiser name" value={dayForm.organiser || ""}
              onChange={(e) => setDayForm((f) => ({ ...f, organiser: e.target.value }))}
              data-testid="day-event-organiser-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            {clients.length > 0 && (
              <select value={dayForm.client_id} onChange={(e) => { const v = e.target.value; setDayForm((f) => ({ ...f, client_id: v, organiser: organiserForClient(v) })); }}
                data-testid="day-event-client-select" title="Client this event is for"
                className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                <option value="">Client: none / not known yet</option>
                {clients.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <select value={dayForm.booking_status} onChange={(e) => setDayForm((f) => ({ ...f, booking_status: e.target.value }))}
              data-testid="day-event-booking-status" title="Provisional until invoiced, then confirmed"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
              <option value="">Confirmed booking</option>
              <option value="provisional">Provisional — not invoiced yet (shows orange)</option>
            </select>
            {vehicles.length > 0 && (
              <select value={dayForm.vehicle_id || ""} onChange={(e) => setDayForm((f) => ({ ...f, vehicle_id: e.target.value }))}
                data-testid="day-event-vehicle-select" title="Which vehicle is going"
                className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                <option value="">Which vehicle: not picked</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate ? ` — ${v.plate}` : ""}{v.kind === "hire" ? " (Hire)" : ""}</option>)}
              </select>
            )}
            <input type="number" min="0" placeholder="Expected attendance" value={dayForm.expected_attendance || ""}
              onChange={(e) => setDayForm((f) => ({ ...f, expected_attendance: e.target.value }))}
              data-testid="day-event-attendance-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input type="date" required value={dayAdd || ""}
              onChange={(e) => setDayAdd(e.target.value)}
              data-testid="day-event-date-input" title="First day"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input type="date" value={dayForm.end_date} min={dayAdd || undefined}
              onChange={(e) => setDayForm((f) => ({ ...f, end_date: e.target.value }))}
              data-testid="day-event-end-date-input" title="Last day (leave blank for one-day event)"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input placeholder="Times (e.g. Fri 9–5, Sat 9–5)" value={dayForm.times}
              onChange={(e) => setDayForm((f) => ({ ...f, times: e.target.value }))}
              data-testid="day-event-times-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <button type="submit" disabled={saving} data-testid="day-event-submit"
              className="h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> {saving ? "Adding…" : "Add event"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={vehOpen} onOpenChange={setVehOpen}>
        <DialogContent className="max-w-sm" data-testid="vehicles-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Vehicles</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Your ambulances, cars & units — pick one against each event.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addVehicle} className="grid grid-cols-[1fr_120px] gap-2">
            <input required placeholder="Vehicle name (e.g. Ambulance 1)" value={vehForm.name}
              onChange={(e) => setVehForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="vehicle-name-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <input placeholder="Number plate" value={vehForm.plate}
              onChange={(e) => setVehForm((f) => ({ ...f, plate: e.target.value }))}
              data-testid="vehicle-plate-input"
              className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <select value={vehForm.kind} onChange={(e) => setVehForm((f) => ({ ...f, kind: e.target.value }))}
              data-testid="vehicle-kind-select"
              className="col-span-2 h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
              <option value="own">My own vehicle</option>
              <option value="hire">Hire vehicle</option>
            </select>
            <button type="submit" data-testid="vehicle-add-btn"
              className="col-span-2 h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> Add vehicle
            </button>
          </form>
          <div className="space-y-2 mt-2">
            {vehicles.length === 0 && <p className="text-xs text-slate-400">No vehicles yet — add your first one above.</p>}
            {vehicles.map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`vehicle-row-${v.id}`}>
                <p className="text-sm font-semibold text-[#0F2547]">{v.name} {v.plate && <span className="ml-1 text-xs font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{v.plate}</span>} <span className={`ml-1 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${v.kind === "hire" ? "bg-amber-100 text-[#ED8B00]" : "bg-emerald-100 text-[#007F3B]"}`}>{v.kind === "hire" ? "Hire" : "Own"}</span></p>
                <button onClick={() => removeVehicle(v)} data-testid={`vehicle-del-${v.id}`} title="Remove vehicle"
                  className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-[#D5281B] hover:bg-red-50 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selected)} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="event-detail-dialog">
          {selected && selected.from_task && (
            <div className="space-y-4" data-testid="job-event-panel">
              <span className="inline-block text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-[#007F3B] border border-emerald-200 px-2 py-1 rounded-full">
                To-do job — not an event
              </span>
              <h2 className="font-heading text-xl font-bold text-[#0F2547]" data-testid="job-event-name">{selected.name}</h2>
              <p className="text-sm text-slate-600">
                {selected.event_date}{selected.times ? ` · ${selected.times}` : ""}
                {selected.client_name ? ` · ${selected.client_name}` : ""}
              </p>
              {linkedTask?.contact_name && (
                <p className="text-sm text-slate-600" data-testid="job-event-contact">Contact: {linkedTask.contact_name}{linkedTask.contact_email ? ` · ${linkedTask.contact_email}` : ""}</p>
              )}
              {(linkedTask?.notes || []).length > 0 && (
                <div className="space-y-1" data-testid="job-event-task-notes">
                  {linkedTask.notes.map((n, i) => (
                    <p key={i} className="text-xs text-slate-500">• {n.text} <span className="text-slate-400">— {n.by}</span></p>
                  ))}
                </div>
              )}
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Notes before it happens</p>
                <div className="flex gap-2">
                  <input value={jobNote} onChange={(e) => setJobNote(e.target.value)} placeholder="Add a note…"
                    data-testid="job-note-input"
                    className="flex-1 h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <button onClick={addJobNote} disabled={!jobNote.trim()} data-testid="job-note-add"
                    className="h-11 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006331] disabled:opacity-40 text-white text-xs font-bold transition-colors">
                    Add note
                  </button>
                </div>
                {(selected.job_notes || []).length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid="job-notes-list">
                    {selected.job_notes.map((n, i) => (
                      <div key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid={`job-note-${i}`}>
                        <span className="text-slate-700">{n.text}</span>
                        <span className="block text-[10px] text-slate-400 mt-0.5">{n.by} · {(n.at || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <input type="date" value={jobDate} onChange={(e) => setJobDate(e.target.value)} data-testid="job-move-date"
                  title="Move this job to a different date" className="h-10 rounded-lg border border-slate-300 px-2 text-xs" />
                <button onClick={moveJob} disabled={!jobDate || !linkedTask} data-testid="job-move-btn"
                  className="h-10 px-3 rounded-lg border border-[#005EB8] text-[#005EB8] text-xs font-bold hover:bg-sky-50 disabled:opacity-40 transition-colors">
                  Move date
                </button>
                <button onClick={markJobDone} data-testid="job-done-btn"
                  className="h-10 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006331] text-white text-xs font-bold transition-colors">
                  Mark done
                </button>
                <button onClick={deleteEvent} data-testid="job-event-delete"
                  className="h-10 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold transition-colors">
                  Remove
                </button>
              </div>
            </div>
          )}
          {selected && !selected.from_task && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-[#005EB8]" /> {selected.name}
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-500 flex items-center gap-3 flex-wrap">
                  {selected.event_date && <span>{selected.event_date}</span>}
                  {selected.venue && (
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.venue)}`}
                      target="_blank" rel="noreferrer" data-testid="event-venue-map-link"
                      className="link-chip">
                      <MapPin className="h-3.5 w-3.5" />{selected.venue}
                    </a>
                  )}
                  {user?.role === "admin" && selected.organiser && <span data-testid="event-organiser-line">Organiser: {selected.organiser}</span>}
                  {user?.role === "admin" && selectedVehicle && <span data-testid="event-vehicle-line" className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5" />{selectedVehicle.name}{selectedVehicle.plate ? ` — ${selectedVehicle.plate}` : ""}</span>}
                  {user?.role === "admin" && selected.client_id && (
                    <button onClick={() => navigate(`/admin/contacts?client=${selected.client_id}`)} data-testid="event-client-contact-btn"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#005EB8] hover:underline transition-colors">
                      <BookUser className="h-3.5 w-3.5" /> Open {selected.client_name || "client"}'s contact card
                    </button>
                  )}
                  {selected.booking_status === "provisional" && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#ED8B00] bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="event-provisional-badge">
                      Provisional — not invoiced yet
                    </span>
                  )}
                  <span className="font-semibold text-[#0F2547]" data-testid="event-attendance-line">
                    {selected.patient_count ?? 0} patients{selected.expected_attendance ? ` of ~${selected.expected_attendance} expected` : ""} · {selected.lost_count ?? 0} lost children
                  </span>
                </DialogDescription>
              </DialogHeader>

              {user?.role === "admin" && selected.booking_status === "provisional" && (
                <div className="grid grid-cols-2 gap-2 mb-3" data-testid="event-booking-decision">
                  <button onClick={confirmBooking} data-testid="event-confirm-booking-btn"
                    className="h-11 rounded-lg bg-[#007F3B] hover:bg-[#006331] transition-colors text-white text-sm font-bold inline-flex items-center justify-center gap-2">
                    <Check className="h-4 w-4" /> Accept — book it in
                  </button>
                  <button onClick={declineBooking} data-testid="event-decline-booking-btn"
                    className="h-11 rounded-lg border-2 border-red-300 text-[#D5281B] hover:bg-red-50 transition-colors text-sm font-bold inline-flex items-center justify-center gap-2 bg-white">
                    <X className="h-4 w-4" /> Decline — remove it
                  </button>
                </div>
              )}

              {(selected.accommodations || []).length > 0 && (
                <div className="mb-3 space-y-2" data-testid="event-hotel-info">
                  {selected.accommodations.map((h) => (
                    <div key={h.id} className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`event-hotel-${h.id}`}>
                      <Hotel className="h-4 w-4 text-[#005EB8] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-[#0F2547]">{user?.role === "admin" ? "Hotel: " : "Your hotel: "}{h.name}</p>
                        <p className="text-[11px] text-slate-500">{[h.address, h.postcode].filter(Boolean).join(", ")}</p>
                        {h.booking_ref && <p className="text-[11px] text-slate-600 mt-0.5">Booking ref: <span className="font-bold" data-testid={`event-hotel-ref-${h.id}`}>{h.booking_ref}</span></p>}
                      </div>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([h.address, h.postcode].filter(Boolean).join(", "))}`}
                        target="_blank" rel="noreferrer" data-testid={`event-hotel-directions-${h.id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-[11px] font-bold transition-colors shrink-0">
                        <Navigation className="h-3.5 w-3.5" /> Directions
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {user?.role === "admin" && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button onClick={() => navigate(`/admin/invoices?event=${selected.id}`)} data-testid="event-make-invoice-btn"
                    className="h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold inline-flex items-center justify-center gap-2">
                    <Wallet className="h-4 w-4" /> Make invoice
                  </button>
                  <button onClick={() => navigate(`/admin/invoices?event=${selected.id}&kind=quote`)} data-testid="event-make-quote-btn"
                    className="h-11 rounded-lg border-2 border-[#005EB8] text-[#005EB8] hover:bg-sky-50 transition-colors text-sm font-bold inline-flex items-center justify-center gap-2 bg-white">
                    Make quote
                  </button>
                </div>
              )}

              {user?.role === "admin" && selected.client_id && (
                <button onClick={openAsk} data-testid="event-askq-btn"
                  className="w-full h-11 mb-3 rounded-lg border-2 border-violet-500 text-violet-700 hover:bg-violet-50 transition-colors text-sm font-bold inline-flex items-center justify-center gap-2 bg-white">
                  <HelpCircle className="h-4 w-4" /> Ask the client questions
                </button>
              )}

              {askOpen && (
                <AskQuestionsDialog
                  task={{ id: "", client_id: selected.client_id || "", event_id: selected.id,
                          contact_name: selected.organiser || "", contact_email: "" }}
                  clients={askClients} events={[selected]} reload={load} onClose={() => setAskOpen(false)} />
              )}

              {user?.role === "admin" && (
                <div className="rounded-xl border border-slate-200 bg-[#F4F7FA] p-3 grid grid-cols-2 gap-2" data-testid="event-edit-form">
                  <input required placeholder="Event name" value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="edit-event-name-input"
                    className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <input placeholder="Venue / location" value={editForm.venue}
                    onChange={(e) => setEditForm((f) => ({ ...f, venue: e.target.value }))}
                    data-testid="edit-event-venue-input"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <input placeholder="Organiser name" value={editForm.organiser}
                    onChange={(e) => setEditForm((f) => ({ ...f, organiser: e.target.value }))}
                    data-testid="edit-event-organiser-input"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <input type="date" value={editForm.event_date}
                    onChange={(e) => setEditForm((f) => ({ ...f, event_date: e.target.value }))}
                    data-testid="edit-event-date-input" title="First day"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <input type="date" value={editForm.end_date} min={editForm.event_date || undefined}
                    onChange={(e) => setEditForm((f) => ({ ...f, end_date: e.target.value }))}
                    data-testid="edit-event-end-date-input" title="Last day (blank = one day)"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <input placeholder="Times (e.g. Fri 9–5, Sat 9–5)" value={editForm.times}
                    onChange={(e) => setEditForm((f) => ({ ...f, times: e.target.value }))}
                    data-testid="edit-event-times-input"
                    className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <input type="number" min="0" placeholder="Expected attendance" value={editForm.expected_attendance}
                    onChange={(e) => setEditForm((f) => ({ ...f, expected_attendance: e.target.value }))}
                    data-testid="edit-event-attendance-input"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <select value={editForm.vehicle_status}
                    onChange={(e) => setEditForm((f) => ({ ...f, vehicle_status: e.target.value }))}
                    data-testid="edit-event-vehicle-select" title="Vehicle for this event"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                    <option value="">Vehicle: not set</option>
                    <option value="own">Own vehicle</option>
                    <option value="booked">Vehicle booked in</option>
                    <option value="needed">Vehicle needed</option>
                  </select>
                  <select value={editForm.vehicle_id || ""}
                    onChange={(e) => setEditForm((f) => ({ ...f, vehicle_id: e.target.value }))}
                    data-testid="edit-event-vehicle-id-select" title="Which vehicle is going"
                    className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                    <option value="">Which vehicle: not picked</option>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate ? ` — ${v.plate}` : ""}{v.kind === "hire" ? " (Hire)" : ""}</option>)}
                  </select>
                  {clients.length > 0 && (
                    <select value={editForm.client_id}
                      onChange={(e) => { const v = e.target.value; setEditForm((f) => ({ ...f, client_id: v, organiser: organiserForClient(v) })); }}
                      data-testid="edit-event-client-select" title="Client this event is for"
                      className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                      <option value="">Client: none / not known yet</option>
                      {clients.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  <select value={editForm.booking_status}
                    onChange={(e) => setEditForm((f) => ({ ...f, booking_status: e.target.value }))}
                    data-testid="edit-event-booking-status" title="Provisional until invoiced, then confirmed"
                    className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]">
                    <option value="">Confirmed booking</option>
                    <option value="provisional">Provisional — not invoiced yet (shows orange)</option>
                  </select>
                  <button onClick={saveEvent} disabled={saving} data-testid="edit-event-save-btn"
                    className="col-span-2 h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              )}

              {user?.role === "admin" && (
                <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 mt-2" data-testid="event-accommodation">
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#005EB8] flex items-center gap-1.5">
                      <Hotel className="h-3.5 w-3.5" /> Hotel / accommodation (admin only)
                    </p>
                    <button type="button" onClick={() => setAccomms((a) => [...a, { name: "", address: "", postcode: "", staff: [] }])}
                      data-testid="accom-add-btn"
                      className="h-8 px-3 rounded-lg bg-[#005EB8] text-white text-[11px] font-bold inline-flex items-center gap-1 hover:bg-[#004a93] transition-colors">
                      <Plus className="h-3.5 w-3.5" /> Add hotel
                    </button>
                  </div>
                  {accomms.length === 0 && (
                    <p className="text-[11px] text-slate-400">No hotel set — add one and tick who's staying. Only ticked staff can see the address.</p>
                  )}
                  <div className="space-y-2">
                    {accomms.map((h, i) => (
                      <div key={i} className="rounded-lg bg-white border border-slate-200 p-2.5 space-y-2" data-testid={`accom-card-${i}`}>
                        <div className="grid grid-cols-2 gap-2">
                          <input placeholder="Hotel name" value={h.name}
                            onChange={(e) => setAccomms((a) => a.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))}
                            data-testid={`accom-name-${i}`}
                            className="h-10 col-span-2 rounded-lg border border-slate-300 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                          <input placeholder="Address" value={h.address}
                            onChange={(e) => setAccomms((a) => a.map((x, xi) => xi === i ? { ...x, address: e.target.value } : x))}
                            data-testid={`accom-address-${i}`}
                            className="h-10 rounded-lg border border-slate-300 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                          <input placeholder="Postcode" value={h.postcode}
                            onChange={(e) => setAccomms((a) => a.map((x, xi) => xi === i ? { ...x, postcode: e.target.value } : x))}
                            data-testid={`accom-postcode-${i}`}
                            className="h-10 rounded-lg border border-slate-300 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                          <input placeholder="Booking ref (e.g. confirmation number)" value={h.booking_ref || ""}
                            onChange={(e) => setAccomms((a) => a.map((x, xi) => xi === i ? { ...x, booking_ref: e.target.value } : x))}
                            data-testid={`accom-booking-ref-${i}`}
                            className="h-10 col-span-2 rounded-lg border border-slate-300 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                        </div>
                        {(selected.roster || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {[...new Set((selected.roster || []).map((r) => r.name).filter(Boolean))].map((n) => {
                              const on = h.staff.includes(n);
                              return (
                                <button type="button" key={n}
                                  onClick={() => setAccomms((a) => a.map((x, xi) => xi === i ? { ...x, staff: on ? x.staff.filter((s) => s !== n) : [...x.staff, n] } : x))}
                                  data-testid={`accom-staff-${i}-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} aria-pressed={on}
                                  className={`h-8 px-2.5 rounded-full border text-[11px] font-bold transition-colors ${on ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-300 text-slate-500 hover:border-[#005EB8]"}`}>
                                  {n}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <button type="button" onClick={() => setAccomms((a) => a.filter((_, xi) => xi !== i))} data-testid={`accom-remove-${i}`}
                          className="text-[11px] font-bold text-[#D5281B] hover:underline inline-flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Remove hotel
                        </button>
                      </div>
                    ))}
                  </div>
                  {accomms.length > 0 && (
                    <button type="button" onClick={saveAccomms} disabled={saving} data-testid="accom-save-btn"
                      className="mt-2 h-10 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006331] text-white text-xs font-bold transition-colors disabled:opacity-60">
                      {saving ? "Saving…" : "Save hotel details"}
                    </button>
                  )}
                </div>
              )}

              {user?.role === "admin" && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 mt-2" data-testid="event-finances">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1.5">
                    <Wallet className="h-3.5 w-3.5" /> Event finances (admin only)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["client_charge", "Client charge £", "fin-client-charge"],
                      ["hotel_cost", "Hotel £", "fin-hotel"],
                      ["fuel_cost", "Fuel / travel £", "fin-fuel"],
                      ["equipment_cost", "Equipment £", "fin-equipment"],
                      ["other_cost", "Other costs £", "fin-other"],
                    ].map(([key, label, testid]) => (
                      <label key={key} className="block">
                        <span className="text-[10px] font-semibold text-slate-500 mb-0.5 block">{label}</span>
                        <input type="number" min="0" step="0.01" value={finForm[key]}
                          onChange={(e) => setFinForm((f) => ({ ...f, [key]: e.target.value }))}
                          data-testid={testid}
                          className="h-10 w-full rounded-lg border border-slate-300 px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      </label>
                    ))}
                    <div className="rounded-lg bg-white border border-slate-200 p-2.5">
                      <span className="text-[10px] font-semibold text-slate-500 block">Staff wages (from rota)</span>
                      <span className="font-vitals font-extrabold text-base text-[#0F2547]" data-testid="fin-wages">
                        £{Number(selected.wages_total || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="rounded-lg bg-white border border-slate-200 p-2.5">
                      <span className="text-[10px] font-semibold text-slate-500 block">Invoiced (auto from invoices)</span>
                      <span className="font-vitals font-extrabold text-base text-[#0F2547]" data-testid="fin-invoiced">
                        £{Number(selected.invoiced_total || 0).toFixed(2)}
                      </span>
                      <span className="block text-[10px] font-semibold text-[#007F3B]" data-testid="fin-received">
                        £{Number(selected.invoiced_received || 0).toFixed(2)} received
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const costs = ["hotel_cost", "fuel_cost", "equipment_cost", "other_cost"].reduce((s, k) => s + (Number(finForm[k]) || 0), 0);
                    const wages = Number(selected.wages_total || 0);
                    const charge = Number(finForm.client_charge) || 0;
                    const invoiced = Number(selected.invoiced_total || 0);
                    const income = invoiced > 0 ? invoiced : charge;
                    const profit = income - costs - wages;
                    const margin = income > 0 ? (profit / income) * 100 : null;
                    return (
                      <div className="grid grid-cols-3 gap-2 mt-2 text-center" data-testid="fin-summary">
                        <div className="rounded-lg bg-white border border-slate-200 p-2">
                          <span className="text-[10px] font-semibold text-slate-500 block">Total costs</span>
                          <span className="font-vitals font-extrabold text-sm text-[#0F2547]" data-testid="fin-costs">£{(costs + wages).toFixed(2)}</span>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-2">
                          <span className="text-[10px] font-semibold text-slate-500 block">Profit</span>
                          <span className={`font-vitals font-extrabold text-sm ${profit >= 0 ? "text-emerald-600" : "text-[#D5281B]"}`} data-testid="fin-profit">
                            £{profit.toFixed(2)}
                          </span>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-2">
                          <span className="text-[10px] font-semibold text-slate-500 block">Margin</span>
                          <span className={`font-vitals font-extrabold text-sm ${margin == null ? "text-slate-400" : margin >= 0 ? "text-emerald-600" : "text-[#D5281B]"}`} data-testid="fin-margin">
                            {margin == null ? "—" : `${margin.toFixed(0)}%`}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  <button onClick={async () => {
                      try {
                        const { data } = await api.patch(`/events/${selected.id}`, { client_paid: !selected.client_paid });
                        setSelected(data);
                        load();
                        toast.success(data.client_paid ? "Marked as client paid" : "Marked as unpaid");
                      } catch (err) {
                        toast.error("Could not update — try again");
                      }
                    }} data-testid="fin-client-paid-toggle"
                    className={`mt-2 w-full h-10 rounded-lg text-sm font-semibold transition-colors ${selected.client_paid ? "bg-emerald-600 text-white" : "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"}`}>
                    {selected.client_paid ? "✓ Client has paid" : "Mark client as paid"}
                  </button>
                  <button onClick={saveFinances} disabled={saving} data-testid="fin-save-btn"
                    className="mt-2 w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 transition-colors text-white text-sm font-semibold disabled:opacity-60">
                    {saving ? "Saving…" : "Save finances"}
                  </button>
                </div>
              )}

              {selected.checkin_token && (
                <div className="rounded-xl border border-slate-200 p-3 text-center" data-testid="calendar-qr-section">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center justify-center gap-1.5">
                    <QrCode className="h-3.5 w-3.5" /> Patient self check-in QR
                  </p>
                  {qrUrl && (
                    <img src={qrUrl} alt={`Check-in QR for ${selected.name}`} data-testid="calendar-qr-image"
                      className="mx-auto w-40 h-40 rounded-lg border border-slate-200 bg-white p-2" />
                  )}
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <button onClick={downloadQr} data-testid="calendar-qr-download"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#005EB8] hover:bg-[#004a93] rounded-lg px-3 py-2 transition-colors min-h-[36px]">
                      <Download className="h-3.5 w-3.5" /> Download QR
                    </button>
                    <button
                      onClick={async () => {
                        const link = `${window.location.origin}/checkin/${selected.checkin_token}`;
                        try { await navigator.clipboard.writeText(link); toast.success("Check-in link copied"); }
                        catch { toast.info(link); }
                      }}
                      data-testid="calendar-qr-copy-link"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#005EB8] border border-slate-300 hover:border-[#005EB8] rounded-lg px-3 py-2 transition-colors min-h-[36px]">
                      <Link2 className="h-3.5 w-3.5" /> Copy link
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Print it or show it on a screen — patients scan to check themselves in.</p>
                </div>
              )}

              {selected.wall_token && (
                <button
                  onClick={async () => {
                    const link = `${window.location.origin}/wall/${selected.wall_token}`;
                    try { await navigator.clipboard.writeText(link); toast.success("Control wall link copied — open it on the big screen"); }
                    catch { toast.info(link); }
                  }}
                  data-testid="wall-link-btn"
                  className="w-full h-11 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Link2 className="h-4 w-4" /> Copy control wall link (big screen — bays & casualties)
                </button>
              )}

              <div className="mt-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Staff booked on this job</p>
                {user?.role !== "admin" ? (
                  (selected.roster || []).length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="roster-staff-empty">No one else booked yet.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="roster-staff-view">
                      {(selected.roster || []).map((r, i) => (
                        <p key={`${r.name}-${r.work_date || "all"}-${i}`} className="text-sm text-[#0F2547]" data-testid={`roster-staff-name-${i}`}>
                          <span className="font-bold">{r.name}</span>
                          <span className="text-slate-500">{r.work_date ? ` — ${fmtDay(r.work_date)}` : ""}{r.time ? ` · ${r.time}` : ""}</span>
                        </p>
                      ))}
                    </div>
                  )
                ) : (
                  <>
                    {(selected.roster || []).length === 0 ? (
                      <p className="text-sm text-slate-500 mb-3" data-testid="no-staff-booked">No one booked yet — add staff or any name below.</p>
                    ) : (
                      <div className="space-y-2 mb-4" data-testid="roster-list">
                        {[...selected.roster].sort((a, b) => (a.work_date || "9999").localeCompare(b.work_date || "9999")).map((r) => (
                          <div key={r.entry_id} className="rounded-xl border border-slate-200 px-4 py-3" data-testid={`roster-entry-${r.entry_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-[#0F2547]">{r.name}</p>
                                {r.required_grade && (
                                  <span className="inline-block text-[10px] font-bold rounded-full px-2 py-0.5 mt-0.5 bg-sky-50 text-[#005EB8]" data-testid={`roster-grade-${r.entry_id}`}>
                                    {GRADES.find((g) => g.value === r.required_grade)?.label}+ required
                                  </span>
                                )}
                                {spanDays(selected).length > 1 && (
                                  <span className={`inline-block text-[10px] font-bold rounded-full px-2 py-0.5 mt-0.5 ${r.work_date ? "bg-emerald-50 text-[#007F3B]" : "bg-slate-100 text-slate-500"}`} data-testid={`roster-day-${r.entry_id}`}>
                                    {r.work_date ? fmtDay(r.work_date) : "All days"}
                                  </span>
                                )}
                                <p className="text-xs text-slate-500 font-vitals">
                                  {r.time || "Time TBC"}
                                  {r.hours != null && r.rate != null && ` · ${r.hours}h @ £${Number(r.rate).toFixed(2)}/h`}
                                  {(() => {
                                    const amt = r.amount != null ? Number(r.amount) : parseFloat(String(r.pay || "").replace(/[^0-9.]/g, "")) || 0;
                                    return amt ? ` · £${amt.toFixed(2)}` : "";
                                  })()}
                                </p>
                                {r.email && <p className="text-[11px] text-slate-400 truncate">{r.email}</p>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => emailRoster(r.entry_id)} disabled={emailBusy === r.entry_id}
                                  data-testid={`roster-email-btn-${r.entry_id}`}
                                  className="text-[#005EB8] hover:bg-sky-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                                  title={r.emailed_at ? "Shift email sent — send again" : "Email shift details"}>
                                  <Mail className="h-4 w-4" />
                                </button>
                                <button onClick={() => removeRoster(r.entry_id)} data-testid={`roster-remove-btn-${r.entry_id}`}
                                  className="text-[#D5281B] hover:bg-red-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                                  title="Remove from job">
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                            {r.emailed_at && (
                              <p className="text-[10px] text-[#007F3B] font-semibold mt-1">
                                Shift email sent {new Date(r.emailed_at).toLocaleString("en-GB")}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {user?.role === "admin" && (
                    <form onSubmit={addRoster} className="rounded-xl border border-slate-200 bg-[#F4F7FA] p-3 grid grid-cols-2 gap-2" data-testid="roster-add-form">
                      <select value={rosterForm.required_grade}
                        onChange={(e) => setRosterForm((f) => ({ ...f, required_grade: e.target.value }))}
                        data-testid="roster-min-grade-select"
                        className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8] text-[#0F2547]">
                        <option value="">Any grade can do this job</option>
                        {GRADES.map((g) => <option key={g.value} value={g.value}>{`Requires ${g.label} or higher`}</option>)}
                      </select>
                      <input required placeholder="Name * — staff or any name" value={rosterForm.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setRosterForm((f) => {
                            const match = staff.find((s) => s.name.toLowerCase() === name.toLowerCase());
                            return { ...f, name, email: f.email || (match ? match.email : "") };
                          });
                        }}
                        list="staff-name-options" autoComplete="off"
                        data-testid="roster-name-input"
                        className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      <datalist id="staff-name-options" data-testid="staff-name-options">
                        {staff
                          .filter((s) => !rosterForm.required_grade || gradeRank(s.grade || "first_aider") >= gradeRank(rosterForm.required_grade))
                          .map((s) => <option key={s.id} value={s.name} />)}
                      </datalist>
                      <input type="email" placeholder="Email (for shift email)" value={rosterForm.email}
                        onChange={(e) => setRosterForm((f) => ({ ...f, email: e.target.value }))}
                        data-testid="roster-email-input"
                        className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      {spanDays(selected).length > 1 && (
                        <select value={rosterForm.work_date}
                          onChange={(e) => setRosterForm((f) => ({ ...f, work_date: e.target.value }))}
                          data-testid="roster-day-select"
                          className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8] text-[#0F2547]">
                          <option value="">{`All days (${fmtDay(selected.event_date)} → ${fmtDay(selected.end_date)})`}</option>
                          {spanDays(selected).map((d) => (
                            <option key={d} value={d}>{fmtDay(d)}</option>
                          ))}
                        </select>
                      )}
                      <input placeholder="Time (e.g. 18:00–23:00)" value={rosterForm.time}
                        onChange={(e) => setRosterForm((f) => ({ ...f, time: e.target.value }))}
                        data-testid="roster-time-input"
                        className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      <input type="number" min="0" step="0.25" placeholder="Hours (e.g. 12)" value={rosterForm.hours}
                        onChange={(e) => setRosterForm((f) => ({ ...f, hours: e.target.value }))}
                        data-testid="roster-hours-input"
                        className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      <input type="number" min="0" step="0.01" placeholder="Rate £/h (e.g. 20)" value={rosterForm.rate}
                        onChange={(e) => setRosterForm((f) => ({ ...f, rate: e.target.value }))}
                        data-testid="roster-rate-input"
                        className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      <p className="col-span-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider -mb-1 text-center">— or a set rate for the shift —</p>
                      <input type="number" min="0" step="0.01" placeholder="Set rate £ (e.g. 240)" value={rosterForm.pay}
                        onChange={(e) => setRosterForm((f) => ({ ...f, pay: e.target.value }))}
                        data-testid="roster-pay-input"
                        className="h-11 col-span-2 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                      {(rosterForm.hours && rosterForm.rate) || rosterForm.pay ? (
                        <p className="col-span-2 text-xs font-bold text-[#0F2547] -mt-1" data-testid="roster-amount-preview">
                          Pay: £{rosterForm.hours && rosterForm.rate
                            ? (Number(rosterForm.hours) * Number(rosterForm.rate)).toFixed(2)
                            : Number(rosterForm.pay || 0).toFixed(2)}
                        </p>
                      ) : null}
                      <button type="submit" disabled={saving} data-testid="roster-add-button"
                        className="col-span-2 h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2">
                        <UserPlus className="h-4 w-4" /> Book onto job
                      </button>
                    </form>
                    )}
                  </>
                )}
              </div>

              <button onClick={() => navigate(`/events/${selected.id}/fight-card`)} data-testid="event-fight-card-btn"
                className="mt-5 w-full h-11 rounded-lg border-2 border-[#0F2547] text-[#0F2547] hover:bg-[#0F2547] hover:text-white transition-colors text-sm font-bold inline-flex items-center justify-center gap-2">
                <Swords className="h-4 w-4" /> Fight card (boxing)
              </button>

              {user?.role === "admin" && (
                <button onClick={toggleArchive} data-testid="archive-event-calendar-btn"
                  className="mt-5 w-full h-11 rounded-lg border border-amber-200 text-xs font-bold text-amber-700 hover:bg-amber-50 transition-colors inline-flex items-center justify-center gap-2">
                  {selected.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  {selected.archived ? "Restore event (unarchive)" : "Archive event (moves to Past events)"}
                </button>
              )}

              {user?.role === "admin" && (
                <button
                  onClick={() => {
                    if (window.confirm(`Delete "${selected.name}"? Its patients will move to Unassigned — nothing is deleted from patient records.`)) deleteEvent();
                  }}
                  data-testid="delete-event-calendar-btn"
                  className="mt-2 w-full h-11 rounded-lg border border-red-200 text-xs font-bold text-[#D5281B] hover:bg-red-50 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" /> Delete event
                </button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
